import { Customer } from 'types/Customer'
import CustomerModel from 'models/Customer'
import Honeybadger from 'honeybadger-js'
import GA4 from 'helpers/ga4'
import { FormData } from 'pages/Customer/SystemReportsModal'
import { DeviceBase } from 'types/Device'

/**
 * Checks if customer name has been updated
 */
const isCustomerNameUpdated = (
  firstName: string,
  lastName: string,
  customer: Customer | null
): boolean => {
  return (
    !!firstName.trim() &&
    !!lastName.trim() &&
    (firstName !== (customer?.firstName ?? '') ||
      lastName !== (customer?.lastName ?? ''))
  )
}

/**
 * Saves updated customer name record if required
 */
const saveUpdatedCustomerName = (
  dealerId: string,
  customer: Customer,
  firstName: string,
  lastName: string,
  isUpdateCustomerRecordChecked: boolean,
  onSaveCallback: (deviceId: string) => void,
  setErrors: (errors: Record<string, string>) => void,
  deviceId: string = ''
): void => {
  if (
    isUpdateCustomerRecordChecked &&
    isCustomerNameUpdated(firstName, lastName, customer)
  ) {
    const customerModel = new CustomerModel()

    const updatedCustomer = {
      ...customer,
      firstName,
      lastName
    }

    const onSaveSuccessCallback = () => {
      onSaveCallback(deviceId)
    }

    const onValidationErrorCallback = (errors) => {
      setErrors(errors)
    }

    const onSaveFailureCallBack = (e) => {
      Honeybadger.notify('Error saving in createCustomerCallback', {
        context: e
      })

      setErrors({
        firstName: 'Failed to save first name',
        lastName: 'Failed to save last name'
      })
    }

    customerModel.save(
      dealerId,
      customer.id,
      updatedCustomer,
      onSaveSuccessCallback,
      onValidationErrorCallback,
      onSaveFailureCallBack
    )
  }
}

/**
 * Validates the form data for customer name and device selection
 */
const validateForm = (
  formData: FormData,
  devices: Array<DeviceBase>,
  setErrors: (errors: Record<string, string>) => void
) => {
  let hasError = false
  const newErrors: {
    firstName?: string
    lastName?: string
    devices?: string
  } = {}

  if (!formData.firstName.trim()) {
    newErrors.firstName = 'First name is required'
    hasError = true
  }

  if (!formData.lastName.trim()) {
    newErrors.lastName = 'Last name is required'
    hasError = true
  }

  if (devices.length > 1 && formData.selectedDeviceIdsAndFileNames.length === 0) {
    newErrors.devices = 'Select at least one system'
    hasError = true
  }

  setErrors(newErrors)
  return hasError
}

/**
 * Tracks a Google Analytics event for actions on the Customer Detail Page.
 * Constructs a label based on formData options and sends the event via GA4.
 */
const trackGoogleAnalyticsEvent = (action: string, formData: FormData) => {
  const {
    useServiceContractStatus,
    useImageOnFile,
    useSaveReportStatus,
    notes
  } = formData
  const label = [
    useImageOnFile ? 'Logo Included' : '',
    useServiceContractStatus ? 'Contract Checked' : '',
    useSaveReportStatus ? 'Report Saved' : '',
    notes ? 'Notes Included' : ''
  ]
    .filter(Boolean)
    .join(', ')

  GA4.sendEvent({ category: 'Customer Detail Page', action, label })
}

export {
  isCustomerNameUpdated,
  saveUpdatedCustomerName,
  validateForm,
  trackGoogleAnalyticsEvent
}
